import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { ref } from 'vue';
import type { IOdds } from '@/interface/IOdds';
import type { BetTypeGroup, LeagueSize, SportType } from '@/interface/enum';
import { MarketType, PriceStyle } from '@/interface/enum';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { useOddsToken } from '@/libraries/oddsApi/composables/useOddsToken';
import { formatOdds } from '@/libraries/oddsApi/oddsApiFormatter';
import { isInvalidLiveMp1X2Odds, isValidPrice, sortOdds } from '@/libraries/oddsDisplay';
import { useAccountStore } from '@/stores/accountStore';
import { useRootStore } from '@/stores/rootStore';
import type { EnumPresetFilterType, OddsSubscriptionType, OddsTypeFragment } from '@/types';
import { EnumOddsCategoryType, useOddsQuery, useOddsSubscription } from '@/types';

type OddsQueryVariable = {
    eventId: MaybeRef<number>;
    oddsId?: MaybeRef<number>;
    betTypeGroupIds?: MaybeRef<BetTypeGroup[] | null>;
    isMixParlayMode: MaybeRef<boolean>;
    presetFilter: MaybeRef<EnumPresetFilterType>;
    isLive?: Ref<boolean>;
    isEnabled: MaybeRef<boolean>;
    marketTypes?: MarketType[];
    sportType: MaybeRef<SportType>;
    leagueSize: MaybeRef<LeagueSize>;
}

export function useOdds(variables: OddsQueryVariable) {
    const oddsList = ref<IOdds[]>([]);

    const { customerInfo } = storeToRefs(useAccountStore());
    const { getOddsToken } = useOddsToken();

    const {
        eventId,
        oddsId,
        betTypeGroupIds,
        presetFilter,
        isMixParlayMode,
        isLive,
        isEnabled,
        marketTypes,
        sportType,
        leagueSize,
    } = variables;

    const oddsVariable = deepCompareComputed(() => {
        const oddsToken = getOddsToken(get(sportType), get(leagueSize));

        return {
            query: {
                id: get(eventId),
                oddsId: get(oddsId),
                marketGroupIds: get(betTypeGroupIds),
                filter: get(presetFilter),
                oddsCategory: get(isMixParlayMode) ? EnumOddsCategoryType.MixParlay : EnumOddsCategoryType.All,
                priceStyle: PriceStyle.toOddsApi(customerInfo.value.priceStyle),
                marketTypes: marketTypes?.map(x => MarketType.toOddsApi(x)),
                oddsToken,
            },
        };
    });

    const { isWsConnected } = storeToRefs(useRootStore());

    function getPollInterval() {
        if (isWsConnected.value) {
            return undefined;
        }
        if (isLive?.value === undefined) {
            return 30000;
        }
        return isLive.value ? 30000 : 60000;
    }

    const { onResult, refetch, loaded, error } = useOddsQuery(
        oddsVariable,
        deepCompareComputed(() => ({
            enabled: get(isEnabled) && !!oddsVariable.value.query.oddsToken,
            pollInterval: getPollInterval(),
        })),
    );

    onResult((data) => {
        if (!data) {
            return;
        }

        const filtered = data
            .filter(e => e.eventResult?.marketGroup !== null)
            .map(e => formatOdds(e))
            .filter(e => isValidPrice(e) && !isInvalidLiveMp1X2Odds(e, get(isMixParlayMode)));
        oddsList.value = sortOdds(filtered);
    });

    const { onSubscription } = useOddsSubscription(
        oddsVariable,
        deepCompareComputed(() => ({
            enabled: get(isEnabled),
        })),
    );

    onSubscription((data) => {
        if (data) {
            oddsList.value = getSubscriptionResult(data, oddsList.value);
        }
    });

    function getSubscriptionResult(newData: (Pick<OddsSubscriptionType, 'deleted'> & { updated: OddsTypeFragment[] }), oldData: IOdds[]): IOdds[] {
        if (!oldData) {
            return [];
        }

        const { updated, deleted } = newData;
        updated
            .filter(e => e.eventResult?.marketGroup !== null)
            .map(e => formatOdds(e))
            .forEach((newOdds) => {
                const oldOddsIndex = oldData.findIndex(
                    oldOdds => newOdds.oddsId === oldOdds.oddsId,
                );
                const isInValidOdds = !isValidPrice(newOdds) || isInvalidLiveMp1X2Odds(newOdds, get(isMixParlayMode));
                if (isInValidOdds) {
                    if (oldOddsIndex !== -1) {
                        oldData.splice(oldOddsIndex, 1);
                    }
                } else if (oldOddsIndex !== -1) {
                    oldData[oldOddsIndex] = newOdds;
                } else {
                    oldData.push(newOdds);
                }
            });

        const filtered = oldData
            .filter(oldOdds => !deleted.includes(oldOdds.oddsId));

        return sortOdds(filtered);
    }

    return {
        loaded,
        oddsList,
        refetch,
        error,
    };
}
